import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Column, useGlobalFilter, usePagination, useTable } from 'react-table'
import { AxiosRequestConfig } from 'axios'
import useDataTableStore from 'store/dataTable'

const EMPTY_CONFIG: AxiosRequestConfig = {}

const useFetch = <T extends object>(
  dataKey: string,
  url?: string,
  cols?: Column<T>[],
  config?: AxiosRequestConfig,
  listName?: string
) => {
  const configRef = useRef(config ?? EMPTY_CONFIG)
  configRef.current = config ?? EMPTY_CONFIG

  const isLoading = useDataTableStore((state) => state.isLoading)
  const rows = useDataTableStore(
    useCallback((state) => state.data[dataKey] as T[], [dataKey])
  )

  const rowsWithList =
    rows && listName && typeof rows === 'object' && listName in rows
      ? (rows as unknown as Record<string, T[]>)[listName]
      : undefined

  const fetch = useDataTableStore((state) => state.fetch)
  const error = useDataTableStore((state) => state.error)
  const setReload = useDataTableStore((state) => state.setReload)

  const columns = useMemo(() => cols || [], [cols])
  const data = useMemo(() => {
    if (listName) {
      return rowsWithList || []
    }

    return rows || []
  }, [rows, rowsWithList, listName])

  const tableInstance = useTable(
    {
      columns,
      data
    },
    useGlobalFilter,
    usePagination
  )

  const refresh = useCallback(() => {
    if (!url) return
    fetch(dataKey, url, configRef.current)
  }, [dataKey, fetch, url])

  useEffect(() => {
    setReload(refresh)
  }, [refresh, setReload])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { isLoading, error, tableInstance, data, refresh }
}

export default useFetch
